/**
 * ExperimentPack 运行期校验器 —— 零依赖手写。
 *
 * 设计原则（与全局 coding-style「在系统边界验证」一致）：
 * 1. 错误**全量收集**后一次返回，作者改实验包时一次看全所有问题，而不是修一个跑一次。
 * 2. 错误消息带路径与可选值，如 `equipment[3].type: 未知器材类型 "TestTub"`。
 * 3. schemaVersion 严格相等，对不上直接拒，不做兼容猜测（Pack 不可变，§22 红线 13/14）。
 */

import { hasTemplate, listTemplateTypes } from "../eom/equipment-registry.js";
import {
  CHEM_EXPERIMENT_TYPES,
  EXAM_POINT_KINDS,
  EXPERIMENT_DIFFICULTIES,
  EXPERIMENT_PACK_SCHEMA_VERSION,
  type ExperimentPack,
} from "./schemas.js";

export type ValidationResult<T> = { ok: true; value: T } | { ok: false; errors: string[] };

const ASSET_KINDS = ["equipment", "reagent", "phenomenon", "ui", "sound"] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function requireString(value: unknown, path: string, errors: string[]): void {
  if (typeof value !== "string" || value.trim() === "") {
    errors.push(`${path}: 必须是非空字符串，实际是 ${JSON.stringify(value)}`);
  }
}

function requireEnum<T extends string>(
  value: unknown,
  allowed: readonly T[],
  path: string,
  errors: string[],
): void {
  if (typeof value !== "string" || !allowed.includes(value as T)) {
    errors.push(`${path}: 取值 ${JSON.stringify(value)} 不在允许集合内，可用：${allowed.join(", ")}`);
  }
}

function requireStringArray(value: unknown, path: string, errors: string[]): void {
  if (!Array.isArray(value) || value.length === 0) {
    errors.push(`${path}: 必须是非空字符串数组`);
    return;
  }
  value.forEach((item, index) => requireString(item, `${path}[${index}]`, errors));
}

/** `{ ref: "..." }` 形态的外部引用，P0 只校验形状，内容由 P2/P3/P4 消费。 */
function validateRef(value: unknown, path: string, errors: string[]): void {
  if (!isRecord(value)) {
    errors.push(`${path}: 必须是 { ref: string } 对象`);
    return;
  }
  requireString(value.ref, `${path}.ref`, errors);
}

function validateRefArray(value: unknown, path: string, errors: string[]): void {
  if (!Array.isArray(value)) {
    errors.push(`${path}: 必须是数组`);
    return;
  }
  value.forEach((item, index) => validateRef(item, `${path}[${index}]`, errors));
}

function validateExamPoints(value: unknown, errors: string[]): void {
  if (!Array.isArray(value)) {
    errors.push("examPoints: 必须是数组");
    return;
  }
  value.forEach((point, index) => {
    const path = `examPoints[${index}]`;
    if (!isRecord(point)) {
      errors.push(`${path}: 必须是对象`);
      return;
    }
    requireString(point.id, `${path}.id`, errors);
    requireString(point.title, `${path}.title`, errors);
    requireEnum(point.kind, EXAM_POINT_KINDS, `${path}.kind`, errors);
    // §22 红线 10：考点必须能从 evidence 回溯到过程事件，所以 evidence 不允许为空
    requireStringArray(point.evidence, `${path}.evidence`, errors);
  });
}

function validateAssetRefs(value: unknown, errors: string[]): void {
  if (!Array.isArray(value)) {
    errors.push("assetRefs: 必须是数组");
    return;
  }
  value.forEach((asset, index) => {
    const path = `assetRefs[${index}]`;
    if (!isRecord(asset)) {
      errors.push(`${path}: 必须是对象`);
      return;
    }
    requireString(asset.ref, `${path}.ref`, errors);
    requireEnum(asset.kind, ASSET_KINDS, `${path}.kind`, errors);
    if (typeof asset.required !== "boolean") {
      errors.push(`${path}.required: 必须是 boolean`);
    }
  });
}

/** 返回本场景启用的分区 id，供 equipment[].zone 交叉校验用。 */
function validateScene(value: unknown, errors: string[]): string[] {
  if (!isRecord(value)) {
    errors.push("scene: 必须是对象");
    return [];
  }
  requireString(value.layout, "scene.layout", errors);
  requireStringArray(value.zones, "scene.zones", errors);
  return Array.isArray(value.zones) ? value.zones.filter((z): z is string => typeof z === "string") : [];
}

function validateEquipment(value: unknown, declaredZones: string[], errors: string[]): void {
  if (!Array.isArray(value) || value.length === 0) {
    errors.push("equipment: 必须是非空数组（没有器材就没有实验）");
    return;
  }

  const seen = new Set<string>();
  value.forEach((item, index) => {
    const path = `equipment[${index}]`;
    if (!isRecord(item)) {
      errors.push(`${path}: 必须是对象`);
      return;
    }
    requireString(item.id, `${path}.id`, errors);
    requireString(item.type, `${path}.type`, errors);

    if (typeof item.id === "string") {
      // id 重复会让后建的对象静默覆盖先建的，必须拦在加载前
      if (seen.has(item.id)) {
        errors.push(`${path}.id: 器材 id "${item.id}" 重复`);
      }
      seen.add(item.id);
    }
    if (typeof item.type === "string" && !hasTemplate(item.type)) {
      errors.push(`${path}.type: 未知器材类型 "${item.type}"，可用类型：${listTemplateTypes().join(", ")}`);
    }
    // zone 写错时 LayoutEngine 会退回实验桌 —— 那是给「模板缺省区未启用」准备的兜底，
    // 不该拿来掩盖实验包里的笔误：器材会莫名其妙出现在别处，作者查半天。这里直接拦。
    if (typeof item.zone === "string" && declaredZones.length > 0 && !declaredZones.includes(item.zone)) {
      errors.push(`${path}.zone: "${item.zone}" 不在 scene.zones 里，本场景启用的分区：${declaredZones.join(", ")}`);
    }
  });
}

function validateTopLevel(pack: Record<string, unknown>, errors: string[]): void {
  if (pack.schemaVersion !== EXPERIMENT_PACK_SCHEMA_VERSION) {
    errors.push(
      `schemaVersion: 必须等于 "${EXPERIMENT_PACK_SCHEMA_VERSION}"，实际是 ${JSON.stringify(pack.schemaVersion)}`,
    );
  }
  requireString(pack.id, "id", errors);
  requireString(pack.title, "title", errors);
  requireString(pack.flowTemplate, "flowTemplate", errors);
  requireStringArray(pack.gradeRange, "gradeRange", errors);
  requireEnum(pack.experimentType, CHEM_EXPERIMENT_TYPES, "experimentType", errors);
  requireEnum(pack.difficulty, EXPERIMENT_DIFFICULTIES, "difficulty", errors);

  // §11.1 声明 subject: "chemistry"，但 §11.3 的示例包省略了它 —— 缺省视为 chemistry，写错则拒。
  if (pack.subject !== undefined && pack.subject !== "chemistry") {
    errors.push(`subject: 只支持 "chemistry"，实际是 ${JSON.stringify(pack.subject)}`);
  }
}

export function validateExperimentPack(raw: unknown): ValidationResult<ExperimentPack> {
  const errors: string[] = [];

  if (!isRecord(raw)) {
    return { ok: false, errors: ["实验包根节点必须是对象"] };
  }

  validateTopLevel(raw, errors);
  validateExamPoints(raw.examPoints, errors);
  validateAssetRefs(raw.assetRefs, errors);
  const declaredZones = validateScene(raw.scene, errors);
  validateEquipment(raw.equipment, declaredZones, errors);
  validateRef(raw.mission, "mission", errors);
  validateRefArray(raw.rules, "rules", errors);
  validateRefArray(raw.knowledge, "knowledge", errors);
  validateRefArray(raw.animations, "animations", errors);
  // assessment 在 §11.1 是必填但 §11.3 示例未含，消费方 P4 才落地：给了才校验
  if (raw.assessment !== undefined) {
    validateRef(raw.assessment, "assessment", errors);
  }

  if (errors.length > 0) {
    return { ok: false, errors };
  }

  return { ok: true, value: { ...raw, subject: "chemistry" } as unknown as ExperimentPack };
}
